<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" hide-details option="Last 30 days"/>
        </v-col>
        <v-col cols="2">
          <v-select :items="['By product','By alcohol/lot']" v-model="by" dense hide-details outlined @change="refresh()"/>
        </v-col>
        <v-col cols="3" v-if="by==='By product'">
          <v-autocomplete label="Product" dense outlined hide-details :items="products" v-model="productId" item-text="name" item-value="id"/>
        </v-col>
        <v-col cols="3" v-else>
          <v-autocomplete label="Alcohol" dense outlined hide-details :items="alcohols" v-model="alcoholId" item-text="name" item-value="id"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Bottling losses', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsBottlingLosses',
  mixins: [printHtml],
  data() {
    return {
      by: 'By product',
      from: null,
      to: null,
      dateDisplay: '',
      products: [{id:null,name:'All'},{divider:true}],
      alcohols: [{id:null,name:'All'},{divider:true}],
      productId: null,
      alcoholId: null,
      loading: true,
      losses: []
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    list() {
      if (this.by === 'By product')
        return this.productId ? this.losses.filter(t => t.id === this.productId) : this.losses;
      else
        return this.alcoholId ? this.losses.filter(t => t.id === this.alcoholId) : this.losses;
    },
  },
  async created() {
    this.$emit('name', 'Bottling losses')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
    const spirits = [];
    const wines = [];
    await Promise.all([
      this.$lazyListLoad(this.products, 'products2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(spirits, 'spirits2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    ]);
    this.alcohols.push(...spirits, {divider:true}, ...wines);

    this.productId = this.$route.query.productId ?? null;
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsBottlingLosses($from:Date!,$to:Date!,$byProduct:Boolean!) {
                                                bottlingLosses(from:$from,to:$to,byProduct:$byProduct) {
                                                  date,name,lot,id,cases,bottles,volume,loss,units
                                                }
                                              }`,
                                          {from:this.from,to:this.to,byProduct:this.by === 'By product'});
      if (!results.error)
        this.losses = results.data.bottlingLosses;
      this.loading = false;

    },

    report() {
      const lines = [
                    `<style>
                      @page {size:landscape}
                      .reports-bottling-losses {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                      .reports-bottling-losses th {height:36px;padding:0 16px;vertical-align:middle}
                      .reports-bottling-losses .spacer {height:18px}
                      .reports-bottling-losses td {height:36px;padding:0 16px;vertical-align:middle}
                      .reports-bottling-losses .title {font-size:18px;text-transform:uppercase}
                      .reports-bottling-losses .sup-title {font-weight:normal;font-size:22px}
                      .reports-bottling-losses .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                      .reports-bottling-losses .header th {border-bottom:1px solid #000;margin-bottom:20px}
                      .reports-bottling-losses .text-left {text-align:left}
                      .reports-bottling-losses .text-right {text-align:right}
                      .reports-bottling-losses .font-weight-bold {font-weight:bold}
                      .reports-bottling-losses .total-row {font-weight:bold}
                    </style>
                    <table class="reports-bottling-losses">
                      <thead>
                          <tr>
                            <th colspan="6" class="sup-title">${this.company.name}</th>
                          </tr>
                          <tr>
                            <th colspan="6" class="title">Bottling losses</th>
                          </tr>
                          <tr>
                            <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                          </tr>

                          <tr class="header">
                              ${this.by === 'By product' ? '<th width="15%" class="text-left">Date</th>' : ''}
                              <th width="30%" class="text-left">${this.by === 'By product' ? 'Product' : 'Alcohol'}</th>
                              ${this.by === 'By product'
                                   ? `<th width="10%" class="text-right">Bottles</th>
                                      <th width="10%" class="text-right">Cases</th>`
                                    : `<th width="20%" class="text-left">Lot</th>`
                                }
                              <th width="12.5%" class="text-right">Produced</th>
                              <th width="12.5%" class="text-right">Loss</th>
                              <th width="10%" class="text-right">%</th>
                            </tr>
                        </thead>
                        <tbody>`
      ];
      let totalLoss = 0;
      let totalProduced = 0;
      let units;
      for (const loss of this.list) {
        lines.push(`<tr>`);
        if (this.by === 'By product')
          lines.push(`<td>${this.$formatDate(loss.date)}</td>`);
        lines.push(`<td>${loss.name}</td>`);
        if (this.by === 'By product') {
          lines.push(`<td class="text-right">${this.$number(loss.bottles,0)}</td>`);
          lines.push(`<td class="text-right">${this.$number(loss.cases,1)}</td>`);
        }
        else
          lines.push(`<td>${loss.lot}</td>`);
        lines.push(`<td class="text-right">${this.$number(loss.volume,3)} ${loss.units}</td>`);
        lines.push(`<td class="text-right">${this.$number(loss.loss,3)} ${loss.units}</td>`);
        lines.push(`<td class="text-right">${this.$number(loss.loss/(loss.volume+loss.loss)*100,1)}%</td>`);
        lines.push(`</tr>`);

        if (units && loss.units !== units)
          units = null;
        else {
          units = loss.units;
          totalLoss += loss.loss;
          totalProduced += loss.volume;
        }
      }

      if (units)
        lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'">
                      <td class="text-right" colspan="${this.by === 'By product' ? 4 : 2}">Total</td>
                      <td class="text-right">${this.$number(totalProduced,3)} ${units}</td>
                      <td class="text-right">${this.$number(totalLoss,3)} ${units}</td>
                      <td class="text-right">${this.$number(totalLoss/(totalProduced+totalLoss)*100,1,3)}%</td>
                    </tr>`);
      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,`,
                      `"Bottling losses",,,,`,
                      `"${this.dateDisplay}",,,,`
                    ];
      if (this.by === 'By product')
        lines.push(`"Date","Product","Bottles","Cases","Produced","Loss","%"`);
      else
        lines.push(`"Alcohol","Lot","Produced","Loss","%"`);
      for (const loss of this.list)
        if (this.by === 'By product')
          lines.push(`"${loss.date}","${loss.name}","${this.$number(loss.bottles,0)}","${this.$number(loss.cases,1)}","${this.$number(loss.volume,3)}","${this.$number(loss.loss,3)}","${this.$number(loss.loss/(loss.volume+loss.loss)*100,1)}"`);
        else
          lines.push(`"${loss.name}","${loss.lot}","${this.$number(loss.volume,3)}","${this.$number(loss.loss,3)}","${this.$number(loss.loss/(loss.volume+loss.loss)*100,1)}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Bottling losses.csv');
    }
  }
}
</script>